import React from 'react'

import {
    DateInput,
    EmptyState,
    FilterDivider,
    FilterButton,
    FilterButtonInner,
    FilterClearButton,
    FilterLabel,
    FilterPanel,
    FilterPanelBody,
    FilterPanelSection,
    FilterRow,
    FilterTags,
    FilterTagsHeader,
    MinCapacitySelect,
    MinCapacitySelectWrap,
    SearchField,
    SearchIcon,
    SearchInput,
    SearchRow,
    TagChip,
    TagList,
    TimeDash,
    TimeInput,
    TimePill,
    TimePills,
} from './styles'
import searchIconUrl from '../../assets/icons/search.svg'
import tuneIconUrl from '../../assets/icons/tune.svg'

import type { RoomFilterTag } from '../../models/rooms'

type FiltersPanelProps = {
    searchTerm: string
    isOpen: boolean
    availableTags: RoomFilterTag[]
    activeTagIds: Set<string>
    minCapacity: number | null
    capacityOptions: number[]
    startTimeLabel: string
    endTimeLabel: string
    scheduleEditor?: {
        dateValue: string
        startTimeValue: string
        endTimeValue: string
        onDateChange: (value: string) => void
        onStartTimeChange: (value: string) => void
        onEndTimeChange: (value: string) => void
    }
    filterPanelRef: React.RefObject<HTMLDivElement | null>
    filterTriggerRef: React.RefObject<HTMLButtonElement | null>
    onSearchChange: (value: string) => void
    onMinCapacityChange: (value: number | null) => void
    onToggleOpen: () => void
    onToggleTag: (tagId: string) => void
    onClearTags: () => void
}

export function FiltersPanel({
    searchTerm,
    isOpen,
    availableTags,
    activeTagIds,
    minCapacity,
    capacityOptions,
    startTimeLabel,
    endTimeLabel,
    scheduleEditor,
    filterPanelRef,
    filterTriggerRef,
    onSearchChange,
    onMinCapacityChange,
    onToggleOpen,
    onToggleTag,
    onClearTags,
}: FiltersPanelProps) {
    return (
        <SearchRow>
            <SearchField htmlFor="room-search">
                <SearchIcon src={searchIconUrl} alt="" aria-hidden="true" />
                <SearchInput
                    id="room-search"
                    type="search"
                    placeholder="Search rooms"
                    autoComplete="off"
                    value={searchTerm}
                    onChange={(event) => onSearchChange(event.target.value)}
                />
            </SearchField>

            <FilterButton
                ref={filterTriggerRef}
                type="button"
                aria-expanded={isOpen}
                aria-controls="filter-panel"
                aria-label="Open filters"
                $active={isOpen}
                onClick={onToggleOpen}
            >
                <FilterButtonInner src={tuneIconUrl} alt="" aria-hidden="true" />
            </FilterButton>

            {isOpen ? (
                <FilterPanel ref={filterPanelRef} id="filter-panel" aria-label="Room filters">
                    <FilterPanelBody>
                        <FilterPanelSection>
                            {scheduleEditor ? (
                                <>
                                    <FilterRow>
                                        <FilterLabel>Date</FilterLabel>
                                        <DateInput
                                            aria-label="Meeting date"
                                            type="date"
                                            value={scheduleEditor.dateValue}
                                            onChange={(event) => scheduleEditor.onDateChange(event.target.value)}
                                        />
                                    </FilterRow>

                                    <FilterDivider />
                                </>
                            ) : null}

                            <FilterRow>
                                <FilterLabel>Time</FilterLabel>
                                <TimePills>
                                    {scheduleEditor ? (
                                        <>
                                            <TimeInput
                                                aria-label="Meeting start time"
                                                type="time"
                                                value={scheduleEditor.startTimeValue}
                                                onChange={(event) => scheduleEditor.onStartTimeChange(event.target.value)}
                                            />
                                            <TimeDash aria-hidden="true" />
                                            <TimeInput
                                                aria-label="Meeting end time"
                                                type="time"
                                                value={scheduleEditor.endTimeValue}
                                                onChange={(event) => scheduleEditor.onEndTimeChange(event.target.value)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <TimePill type="button">{startTimeLabel}</TimePill>
                                            <TimeDash aria-hidden="true" />
                                            <TimePill type="button">{endTimeLabel}</TimePill>
                                        </>
                                    )}
                                </TimePills>
                            </FilterRow>
                        </FilterPanelSection>

                        <FilterDivider />

                        <FilterPanelSection>
                            <FilterRow>
                                <FilterLabel>Min capacity</FilterLabel>
                                <MinCapacitySelectWrap>
                                    <MinCapacitySelect
                                        aria-label="Minimum capacity"
                                        value={minCapacity ?? ''}
                                        onChange={(event) => {
                                            const value = event.target.value
                                            onMinCapacityChange(value ? Number(value) : null)
                                        }}
                                    >
                                        <option value="">Any</option>
                                        {capacityOptions.map((capacity) => (
                                            <option key={capacity} value={capacity}>
                                                {capacity}
                                            </option>
                                        ))}
                                    </MinCapacitySelect>
                                </MinCapacitySelectWrap>
                            </FilterRow>
                        </FilterPanelSection>

                        <FilterDivider />

                        <FilterTags>
                            <FilterTagsHeader>
                                <FilterLabel>Tags</FilterLabel>
                                <FilterClearButton type="button" onClick={onClearTags}>
                                    Clear
                                </FilterClearButton>
                            </FilterTagsHeader>

                            <TagList>
                                {availableTags.length ? (
                                    availableTags.map((tag) => (
                                        <TagChip
                                            key={tag.id}
                                            type="button"
                                            $active={activeTagIds.has(tag.id)}
                                            onClick={() => onToggleTag(tag.id)}
                                        >
                                            {tag.name}
                                        </TagChip>
                                    ))
                                ) : (
                                    <EmptyState>No tags found</EmptyState>
                                )}
                            </TagList>
                        </FilterTags>
                    </FilterPanelBody>
                </FilterPanel>
            ) : null}
        </SearchRow>
    )
}
